import { Centrifuge, type PublicationContext, SubscriptionState } from "centrifuge"
import { CentrifugeSubscriptionType } from "~/enums"

const centrifuge: Ref<Centrifuge | undefined> = ref(undefined)

// A Subscription outlives the component that created it, so its handlers look the listener up per
// publication instead of binding the creator's. Cost: one listener per channel — a second
// subscriber replaces the first, and either one's unsubscribe ends it for both.
type Listener = { callback: (data: never) => void; onResync?: () => void }
const listeners = new Map<string, Listener>()

export function useCentrifuge() {
	const { user } = useUserContext()
	const { getAccessToken, accessToken } = useAccessToken()
	const runtimeCfg = useRuntimeConfig()

	const initializeCentrifuge = () => {
		if (centrifuge.value == null) {
			centrifuge.value = new Centrifuge(runtimeCfg.public.wsUrl as string, {
				token: accessToken.value || "",
				getToken: async () => {
					try {
						return await getAccessToken()
					} catch (error) {
						console.error("Failed to refresh token:", error)
						throw error
					}
				}
			})
			centrifuge.value
				.on("connecting", (ctx) => {
					log(`connecting: ${ctx.code}, ${ctx.reason}`)
				})
				.on("connected", (ctx) => {
					log(`connected over ${ctx.transport}`)
				})
				.on("disconnected", (ctx) => {
					log(`disconnected: ${ctx.code}, ${ctx.reason}`)
				})
				.connect()
		}
	}

	const getToken = async (subType: CentrifugeSubscriptionType, id: string): Promise<string> => {
		let channel: string
		switch (subType) {
			case CentrifugeSubscriptionType.fleet: {
				channel = `/api/channels/token/fleet/${id}`
				break
			}
			case CentrifugeSubscriptionType.app: {
				channel = `/api/channels/token/app/${id}`
				break
			}
			case CentrifugeSubscriptionType.device: {
				channel = `/api/channels/token/device/${id}`
				break
			}
			case CentrifugeSubscriptionType.deviceCreated: {
				channel = `/api/channels/token/devicecreate/${id}`
				break
			}
			case CentrifugeSubscriptionType.osgen: {
				channel = `/api/channels/token/osgen/${id}`
				break
			}
			case CentrifugeSubscriptionType.metric: {
				channel = `/api/channels/token/metric/${id}`
				break
			}
			case CentrifugeSubscriptionType.updateGroup: {
				channel = `/api/channels/token/updateGroup/${id}`
				break
			}
			default: {
				const { user } = useUserContext()
				channel = `/api/channels/token/notifications/${user.value.tenant}`
			}
		}
		return await $fetch<string>(channel, {
			method: "GET"
		})
	}

	// `onResync` fires when a subscription re-establishes after a reconnect but
	// Centrifugo could NOT replay the missed publications (history gap, or a
	// Centrifugo restart that changed the stream epoch). Realtime alone can't
	// fill that gap, so the caller should pull authoritative state (refetch).
	const subscribe = async <T>(callback: (data: T) => void, subType: CentrifugeSubscriptionType, id: string, onResync?: () => void) => {
		if (id === "") {
			id = user.value.email
		}
		const channel = `${runtimeCfg.public.centrifugoChannelPrefix}-${user.value.tenant}:${id}`
		// Race fix: page `onMounted` can fire before layout `onMounted` has called
		// `initializeCentrifuge()`. Without this guard, subscriptions on /fleets/:id
		// and /fleets/:id/devices/:id silently no-op after a hard refresh.
		if (!centrifuge.value) {
			initializeCentrifuge()
		}
		if (!centrifuge.value) {
			return undefined
		}
		listeners.set(channel, { callback: callback as Listener["callback"], onResync })
		let currentSub = centrifuge.value.getSubscription(channel)
		if (currentSub === null) {
			currentSub = centrifuge.value.newSubscription(channel, {
				getToken: async () => {
					return await getToken(subType, id)
				}
			})
			currentSub
				.on("publication", (ctx: PublicationContext) => {
					try {
						log(`publication ${ctx.channel}`, ctx.data)
						listeners.get(ctx.channel)?.callback(ctx.data as never)
					} catch (error) {
						// Never let a bad payload kill the listener for this channel.
						console.error(`Centrifugo publication handler failed for ${ctx.channel}`, error)
					}
				})
				.on("subscribing", (ctx) => {
					log(`subscribing: ${ctx.channel}, ${ctx.code}, ${ctx.reason}`)
				})
				.on("subscribed", (ctx) => {
					log(`subscribed ${ctx.channel}`, ctx)
					// First-time subscribe: wasRecovering=false. Successful
					// recovery: recovered=true. Only a failed recovery needs a
					// resync — the gap is unrecoverable from history.
					if (ctx.wasRecovering && !ctx.recovered) {
						try {
							listeners.get(ctx.channel)?.onResync?.()
						} catch (error) {
							console.error(`Centrifugo resync failed for ${ctx.channel}`, error)
						}
					}
				})
				.on("unsubscribed", (ctx) => {
					log(`unsubscribed: ${ctx.channel}, ${ctx.code}, ${ctx.reason}`)
				})
		}
		if (currentSub.state === SubscriptionState.Unsubscribed) {
			currentSub.subscribe()
		}
	}

	const unsubscribe = (id: string) => {
		if (id === "") {
			id = user.value.email
		}
		const channel = `${runtimeCfg.public.centrifugoChannelPrefix}-${user.value.tenant}:${id}`
		listeners.delete(channel)
		centrifuge.value?.removeSubscription(centrifuge.value.getSubscription(channel))
	}

	return { subscribe, unsubscribe, initializeCentrifuge }
}
